'use client';

import { useMemo } from 'react';
import { analytics } from '@/lib/analytics/registry';
import type { ConsentLevel, EventCategory } from '@/lib/analytics/types';
import { useConsent } from '@/hooks/use-consent';

function consentLevel(consent: string | null): ConsentLevel {
  if (consent === 'accepted') return 'marketing'; // highest: everything fires
  return 'essential'; // rejected / null / pending: only essential adapters
}

function emit(
  name: string,
  category: EventCategory,
  consent: string | null,
  properties?: Record<string, unknown>,
) {
  analytics.track({ name, category, properties }, consentLevel(consent));
}

export function useAnalytics() {
  const { consent } = useConsent();

  return useMemo(() => ({
    // ── Navigation ──────────────────────────────────────────────
    trackNavClick: (target: string, source?: string) =>
      emit('nav_click', 'navigation', consent, { target, source }),
    trackTabSwitch: (tab: string, source?: string) =>
      emit('tab_switch', 'navigation', consent, { tab, source }),
    trackSearch: (query: string, resultCount?: number) =>
      emit('search_submit', 'engagement', consent, { query, resultCount }),
    trackSearchOpen: () =>
      emit('search_open', 'navigation', consent),
    trackSearchResultClick: (query: string, resultId: string, position?: number) =>
      emit('search_result_click', 'engagement', consent, { query, resultId, position }),
    trackSearchSuggestionClick: (suggestion: string) =>
      emit('search_suggestion_click', 'engagement', consent, { suggestion }),

    // ── Journey ─────────────────────────────────────────────────
    trackPageView: (path: string, referrer?: string) => {
      emit('page_view', 'journey', consent, { path, referrer });
      analytics.page(path, consentLevel(consent));
    },
    trackSignUp: (method: string) =>
      emit('sign_up', 'journey', consent, { method }),
    // Splash cohort assignment (from a /for/<slug> invite, ?cohort= link, or UTM).
    trackCohort: (cohort: string, source: string) =>
      emit('cohort_assigned', 'journey', consent, { cohort, source }),
    trackFirstPost: () =>
      emit('first_post', 'journey', consent),
    trackAgentSearch: (query: string) =>
      emit('agent_search', 'journey', consent, { query }),
    trackAgentView: (agentId: string) =>
      emit('agent_view', 'journey', consent, { agentId }),
    trackSubscribe: (agentId: string, amount?: number) =>
      emit('subscribe', 'journey', consent, { agentId, amount }),
    trackPaymentApprove: (agentId: string, amount: number) =>
      emit('payment_approve', 'journey', consent, { agentId, amount }),
    trackPaymentDeny: (agentId: string) =>
      emit('payment_deny', 'journey', consent, { agentId }),

    // ── Engagement: posts ───────────────────────────────────────
    trackPostView: (postId: string, source?: string) =>
      emit('post_view', 'engagement', consent, { targetId: postId, source }),
    trackPostClick: (postId: string) =>
      emit('post_click', 'engagement', consent, { targetId: postId }),
    trackPostLike: (postId: string) =>
      emit('post_like', 'engagement', consent, { targetId: postId }),
    trackPostRepost: (postId: string) =>
      emit('post_repost', 'engagement', consent, { targetId: postId }),
    trackPostShare: (postId: string, method?: 'native' | 'clipboard') =>
      emit('post_share', 'engagement', consent, { targetId: postId, method }),
    trackPostComment: (postId: string) =>
      emit('post_comment', 'engagement', consent, { targetId: postId }),
    trackPostDwell: (postId: string, dwellTime: number) =>
      emit('post_dwell', 'engagement', consent, { targetId: postId, dwellTime }),

    // ── Engagement: social ──────────────────────────────────────
    trackFollow: (targetType: 'user' | 'channel' | 'tag', targetId: string) =>
      emit(`${targetType}_follow`, 'engagement', consent, { targetId }),
    trackUnfollow: (targetType: 'user' | 'channel' | 'tag', targetId: string) =>
      emit(`${targetType}_unfollow`, 'engagement', consent, { targetId }),
    trackSignal: (postId: string, direction: 'up' | 'down') =>
      emit('signal_vote', 'engagement', consent, { targetId: postId, direction }),

    // ── Engagement: chat ────────────────────────────────────────
    trackChatOpen: (chatId?: string) =>
      emit('chat_open', 'engagement', consent, { chatId }),
    trackChatSend: (chatId: string) =>
      emit('chat_send', 'engagement', consent, { chatId }),
    trackChatStop: (chatId: string) =>
      emit('chat_stop', 'engagement', consent, { chatId }),
    trackChatSatisfaction: (chatId: string, rating: 'positive' | 'negative') =>
      emit('chat_satisfaction', 'engagement', consent, { chatId, rating }),
    trackVoiceAction: (action: 'mute' | 'unmute' | 'end' | 'speaker_toggle') =>
      emit('voice_action', 'engagement', consent, { action }),

    // ── Engagement: agents ──────────────────────────────────────
    trackToolApproval: (agentId: string, toolName: string, decision: 'approve_once' | 'approve_always' | 'deny' | 'block') =>
      emit('tool_approval', 'engagement', consent, { agentId, toolName, decision }),
    trackAgentIntegration: (agentId: string, integration: string, action: 'enable' | 'disable') =>
      emit('agent_integration', 'engagement', consent, { agentId, integration, action }),

    // ── Engagement: content ─────────────────────────────────────
    trackContentAction: (contentId: string, action: string) =>
      emit('content_action', 'engagement', consent, { contentId, action }),
    trackCopy: (what: string, targetId?: string) =>
      emit('clipboard_copy', 'engagement', consent, { what, targetId }),

    // ── Commerce ────────────────────────────────────────────────
    trackCheckoutStart: (plan?: string) =>
      emit('checkout_start', 'commerce', consent, { plan }),
    trackSubscriptionManage: () =>
      emit('subscription_manage', 'commerce', consent),
    trackSpendingLimitChange: (newLimit?: number) =>
      emit('spending_limit_change', 'commerce', consent, { newLimit }),
    trackReferralShare: (method?: string) =>
      emit('referral_share', 'commerce', consent, { method }),

    // ── Create flows ────────────────────────────────────────────
    trackCreateStart: (type: 'post' | 'agent' | 'channel') =>
      emit('create_start', 'engagement', consent, { type }),
    trackCreateSubmit: (type: 'post' | 'agent' | 'channel') =>
      emit('create_submit', 'engagement', consent, { type }),
    trackCreateCancel: (type: 'post' | 'agent' | 'channel') =>
      emit('create_cancel', 'engagement', consent, { type }),

    // ── Auth ────────────────────────────────────────────────────
    trackAuthStart: (provider: string) =>
      emit('auth_start', 'journey', consent, { provider }),
    trackOAuthDecision: (decision: 'authorize' | 'deny', appId?: string) =>
      emit('oauth_decision', 'journey', consent, { decision, appId }),

    // ── Settings ────────────────────────────────────────────────
    trackSettingsAction: (action: string, detail?: string) =>
      emit('settings_action', 'engagement', consent, { action, detail }),

    // ── Modals / Prompts ────────────────────────────────────────
    trackPromptResponse: (prompt: string, response: 'accept' | 'dismiss' | 'submit') =>
      emit('prompt_response', 'engagement', consent, { prompt, response }),
    trackConsentChoice: (choice: 'accept_all' | 'save_preferences' | 'reject') =>
      emit('consent_choice', 'engagement', consent, { choice }),

    // ── Discovery / Feed ────────────────────────────────────────
    trackFilterChange: (filterType: string, value: string) =>
      emit('filter_change', 'engagement', consent, { filterType, value }),
    trackSortChange: (sortValue: string) =>
      emit('sort_change', 'engagement', consent, { sortValue }),

    // ── Error recovery ──────────────────────────────────────────
    trackErrorRetry: (page: string) =>
      emit('error_retry', 'engagement', consent, { page }),

    // ── Docs feedback ───────────────────────────────────────────
    trackDocsFeedback: (page: string, rating: 'positive' | 'negative') =>
      emit('docs_feedback', 'engagement', consent, { page, rating }),

    // ── Identity (call from auth transitions) ───────────────────
    identify: (userId: string, traits?: Record<string, unknown>) =>
      analytics.identify(userId, consentLevel(consent), traits),
    reset: () =>
      analytics.reset(consentLevel(consent)),

    // ── Web Vitals (internal-only, no consent check) ────────────
    trackWebVital: (vitalName: string, vitalValue: number, vitalRating: string) =>
      emit('web_vital', 'vitals', consent, { vitalName, vitalValue, vitalRating }),
  }), [consent]);
}
